/**
 * officeGroups — s123 (Dr. Rami 2026-10-29, canvas "Orella Doctors & Clinicians Home" board F APPROVED: "can we search by
 * practice group name? ... should they select a specific doctor in the group to send to?"; his ruling: the practice card →
 * pick the office → "Any clinician at this office" by default, a specific clinician optional). PURE.
 *
 * Measured on PROD 2026-10-30: 73 clinicians share 401 Bicentennial Way, so the map drew 73 pins on one spot. Here the
 * directory's rows (GET /providers/search) become OFFICES: one per confirmed street address (the API's own address key,
 * line 1 + city + state + ZIP 5, upper-cased), and PRACTICES: the offices of one practice name (row.practice, the
 * office's org name on file).
 *   officeKey(row)            the office a row belongs to ('npi-<npi>' when its address is not confirmed or has no street)
 *   groupOffices(rows)        [{ key, rows (nearest first), rep (the nearest row with a fax, else the nearest), practice,
 *                             miles, lat, lng, placed, address }] nearest office first
 *   practicesFromRows(rows)   [{ name, offices, clinicians, miles }] for the rows a typed name matched by PRACTICE
 *                             (matchKind 'practice'), nearest practice first
 *   officeSendTarget(office, { row })  what StartCaseSheet / the share hub send to: a chosen clinician's row, or — no
 *                             row — the office itself for "Any clinician at this office" (anyClinician: true; the fax
 *                             cover then reads "To: Any available clinician" over the practice, English, D483)
 */
const norm = (v) => String(v || '').replace(/\s+/g, ' ').trim().toUpperCase()

export function officeKey(r) {
  if (!r) return ''
  const a = r.address || {}
  if (r.addressConfirmed === false || !a.line1) return `npi-${r.npi}`
  return ['A', norm(a.line1), norm(a.city), norm(a.state), String(a.zip || '').slice(0, 5)].join('|')
}

const milesOf = (r) => (typeof r?.miles === 'number' && isFinite(r.miles) ? r.miles : Infinity)

function mostCommon(list) {
  const n = new Map()
  for (const v of list) if (v) n.set(v, (n.get(v) || 0) + 1)
  let best = null, c = 0
  for (const [v, k] of n) if (k > c || (k === c && String(v) < String(best))) { best = v; c = k }
  return best
}

export function groupOffices(rows) {
  const by = new Map()
  for (const r of rows || []) {
    if (!r || !r.npi) continue
    const k = officeKey(r)
    if (!by.has(k)) by.set(k, [])
    by.get(k).push(r)
  }
  const out = []
  for (const [key, list] of by) {
    const sorted = [...list].sort((a, b) => milesOf(a) - milesOf(b) || String(a.name || '').localeCompare(String(b.name || '')))
    const rep = sorted.find((r) => r.faxOnFile) || sorted[0]
    out.push({
      key, rows: sorted, rep, practice: mostCommon(sorted.map((r) => r.practice)),
      miles: milesOf(sorted[0]), lat: rep.lat, lng: rep.lng, placed: rep.placed, address: rep.address || null,
    })
  }
  return out.sort((a, b) => a.miles - b.miles || a.key.localeCompare(b.key))
}

export function practicesFromRows(rows) {
  const matched = (rows || []).filter((r) => r && r.matchKind === 'practice' && r.practice)
  const by = new Map()
  for (const r of matched) {
    const k = norm(r.practice)
    if (!by.has(k)) by.set(k, [])
    by.get(k).push(r)
  }
  const out = []
  for (const list of by.values()) {
    const offices = groupOffices(list)
    out.push({ name: mostCommon(list.map((r) => r.practice)), offices, clinicians: new Set(list.map((r) => r.npi)).size, miles: offices.length ? offices[0].miles : Infinity })
  }
  return out.sort((a, b) => a.miles - b.miles || String(a.name).localeCompare(String(b.name)))
}

export function officeSendTarget(office, { row = null } = {}) {
  if (!office) return null
  if (row) return row
  const rep = office.rep || (office.rows || [])[0]
  if (!rep) return null
  return { ...rep, name: office.practice || rep.name, credential: null, practice: office.practice || rep.practice || null, anyClinician: true }
}
